import { Helmet } from "react-helmet-async";
import { SITE_URL, SITE_NAME, DEFAULT_OG_IMAGE } from "@/lib/seo-constants";

/**
 * <Seo> — title, meta, canonical, OG/Twitter, hreflang, geo, JSON-LD.
 *
 *   <Seo
 *     title="..."  description="..."  path="/route"
 *     image="/og.jpg"                                // optional OG image
 *     jsonLd={[ ... ]}                               // schema.org objects
 *     noindex={true}                                 // /admin, /404
 *     geo={{ region: 'CA-ON', placename: 'North York', lat: 43.7644, lon: -79.4164 }}
 *     preloadImage="/projects/fencing/foo.webp"      // preload above-fold hero
 *   />
 */
const Seo = ({
    title,
    description,
    path = "/",
    image = DEFAULT_OG_IMAGE,
    jsonLd = [],
    noindex = false,
    geo = null,
    preloadImage = null,
}) => {
    const fullTitle = title || SITE_NAME;
    const canonical = `${SITE_URL}${path}`;
    const ogImage = image.startsWith("http") ? image : `${SITE_URL}${image}`;

    return (
        <Helmet>
            <title>{fullTitle}</title>
            <meta name="description" content={description} />
            <link rel="canonical" href={canonical} />
            {noindex ? (
                <meta name="robots" content="noindex, nofollow" />
            ) : (
                <meta name="robots" content="index, follow" />
            )}

            {/* hreflang */}
            <link rel="alternate" hrefLang="en-CA" href={canonical} />
            <link rel="alternate" hrefLang="x-default" href={canonical} />

            {/* Geo meta — only emitted when explicitly provided per-page */}
            {geo && (
                <meta name="geo.region" content={geo.region || "CA-ON"} />
            )}
            {geo && geo.placename && (
                <meta name="geo.placename" content={geo.placename} />
            )}
            {geo && geo.lat != null && geo.lon != null && (
                <meta
                    name="geo.position"
                    content={`${geo.lat};${geo.lon}`}
                />
            )}
            {geo && geo.lat != null && geo.lon != null && (
                <meta
                    name="ICBM"
                    content={`${geo.lat}, ${geo.lon}`}
                />
            )}

            {/* Open Graph */}
            <meta property="og:type" content="website" />
            <meta property="og:site_name" content={SITE_NAME} />
            <meta property="og:title" content={fullTitle} />
            <meta property="og:description" content={description} />
            <meta property="og:url" content={canonical} />
            <meta property="og:image" content={ogImage} />
            <meta property="og:locale" content="en_CA" />
            <meta property="article:publisher" content={SITE_URL} />

            {/* Twitter card */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={fullTitle} />
            <meta name="twitter:description" content={description} />
            <meta name="twitter:image" content={ogImage} />

            {/* Preload above-the-fold hero */}
            {preloadImage && (
                <link
                    rel="preload"
                    as="image"
                    href={preloadImage}
                    fetchpriority="high"
                />
            )}

            {/* JSON-LD structured data */}
            {jsonLd.map((schema, i) => (
                <script key={i} type="application/ld+json">
                    {JSON.stringify(schema)}
                </script>
            ))}
        </Helmet>
    );
};

export default Seo;
